import get from 'lodash/get'
import { queryArgs, isNoCampaignsError } from '@/utils'
import { defaultState } from './state'

class ResourcesError extends Error {
	constructor (message, response) {
		super(message)
		this.response = response
		this.name = 'ResourcesError'
	}
}

export default {
	fetchWpResources ({ commit, dispatch, state, rootGetters }, { exclude = [] }) {
		let wpEndpoint = 'omapp/v1/resources'
		let params = ''
		if (!rootGetters.isFetched('wpResources')) {
			params = queryArgs.add('refresh', 1, params)
		}

		if (exclude.length) {
			params = queryArgs.add('excluded', exclude.join(','), params)
		}

		if (params.length) {
			wpEndpoint += '?' + params
		}

		// 	if (rootGetters.isFetched('wpResources') || rootGetters.isFetching('wpResources')) {
		// 		resolve()
		// 	}

		commit('fetching', 'wpResources', { root: true })

		return new Promise((resolve, reject) => {
			return this._vm.$api
				.get({ wpEndpoint, cached: false })
				.then((response) => {
					const { body } = response

					if (!get(body, 'config')) {
						throw new ResourcesError(
							'No config found in the response (missing response data). There might be an issue with your WordPress sites\'s REST API settings.',
							response
						)
					}

					commit('setError', false)
					commit('fetched', 'wpResources', { root: true })

					if (!exclude.includes('campaigns')) {
						if (!get(body, 'campaigns')) {
							throw new ResourcesError(
								'No campaigns found in the response.',
								response
							)
						}

						dispatch('campaigns/updateCampaignsWithWpData', body.campaigns, { root: true })
						commit('setWpCampaigns', body.campaigns)
					}

					const toUpdate = {
						config : {
							mutation   : 'setWpConfig',
							canExclude : false,
						},
						taxonomies : {
							mutation   : 'setWpTaxonomies',
							canExclude : true,
						},
						posts : {
							mutation   : 'setWpPosts',
							canExclude : true,
						},
						post_types : {
							mutation   : 'setWpPostTypes',
							canExclude : true,
						},
					}
					Object.keys(toUpdate).forEach(k => {
						const prop         = toUpdate[k]
						const canExclude   = !prop.canExclude
						const shouldCommit = !canExclude || !exclude.includes(k)

						if (!shouldCommit) {
							return
						}

						if (!get(body, k)) {
							throw new ResourcesError(
								`No ${k} found in the response. There might be an issue with your WordPress site's `,
								response
							)
						}

						return commit(prop.mutation, body[k])
					})

					this._vm.$bus.$emit('fetchedWpResources', body)
					resolve(body)
				})
				.catch(error => {
					error.explanation = 'Something went wrong while fetching WordPress resources.'

					if (!isNoCampaignsError(error)) {
						// WP-specific error.
						commit('setError', true)
						error.closes = false
						commit('maybeAddError', error, { root: true })
					}

					reject(error)
				})
		})
	},
	autogenerateWooCreds ({ commit, dispatch }) {
		return dispatch('saveWooCreds', { auto: true })
	},
	saveWooCreds ({ commit }, { key, secret, auto = false }) {
		commit('loading', 'wooconnect', { root: true })
		return new Promise((resolve, reject) => {
			this._vm.$api
				.post({ wpEndpoint: `omapp/v1/woocommerce/${auto ? 'autogenerate' : 'save'}` })
				.send({ key, secret })
				.then(({ body }) => {
					commit('doneLoading', 'wooconnect', { root: true })

					commit('fetched', 'wooKey', { root: true })
					commit('setWooConnected', Boolean(body.id))
					commit('setWooKey', body)

					this._vm.$bus.$emit('wooKeyFetched', body)

					resolve()
				})
				.catch((error) => {
					commit('closeAlerts', null, { root: true })

					commit('setWooConnected', false)
					error.explanation = `<strong>Something went wrong while attempting to ${auto ? 'autogenerate' : 'save'} the WooCommerce Keys.</strong>`
					if (this._vm.$env.isDevelopment()) {
						error.explanation += '<p>This is likely because you are in the <code>DEVELOPMENT</code> environment, and cookies could not be passed to the auto-generation request.</p>'
					}
					error.closes = true
					commit('setError', error, { root: true })
					commit('doneLoading', 'wooconnect', { root: true })

					reject(error)
					console.error({ error })
				})
		})
	},
	disconnectWoo ({ commit, dispatch }) {
		commit('loading', 'wooconnect', { root: true })
		return new Promise((resolve, reject) => {
			return this._vm.$api
				.post({ wpEndpoint: 'omapp/v1/woocommerce/disconnect' })
				.then(() => {
					commit('setWooConnected', false)
					commit('setWooKey', defaultState().wooKey)
					commit('doneLoading', 'wooconnect', { root: true })

					this._vm.$bus.$emit('wooKeyRemoved')

					resolve()
				})
				.catch((error) => {
					commit('closeAlerts', null, { root: true })

					error.explanation = '<strong>Something went wrong while attempting to disconnect WooCommerce.</strong>'
					error.closes = true
					commit('setError', error, { root: true })
					commit('doneLoading', 'wooconnect', { root: true })
					reject(error)
					console.error({ error })
				})
		})
	},
	fetchWooKey ({ getters, commit, rootGetters }, force = false) {
		if (!getters.isWooConnected) {
			return
		}

		return new Promise((resolve, reject) => {
			if (
				!force &&
				(
					rootGetters.isFetched('wooKey') ||
					rootGetters.isFetching('wooKey')
				)
			) {
				return resolve()
			}

			commit('fetching', 'wooKey', { root: true })

			return this._vm.$api
				.get({ wpEndpoint: 'omapp/v1/woocommerce/key' })
				.then(({ body }) => {
					commit('fetched', 'wooKey', { root: true })
					commit('setWooConnected', Boolean(body.id))
					commit('setWooKey', body)
					this._vm.$bus.$emit('wooKeyFetched', body)
					resolve()
				})
				.catch(error => {
					error.explanation = '<strong>Something went wrong while attempting to disconnect WooCommerce.</strong>'
					reject(error)
					console.error({ error })
				})
		})
	},
}
